// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { 
	Box, 
	Image, 
	Divider, 
	Button, 
	Grid, 
	Text, 
	Flex,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalContent,
	useColorModeValue,
	useBreakpointValue,
	useDisclosure,
	useColorMode
} from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';
import { LuInstagram } from 'react-icons/lu';
import { RiTwitterXFill } from 'react-icons/ri';
import { MdFacebook } from 'react-icons/md';
import { FaYoutube, FaTiktok } from 'react-icons/fa';

// Assets
import { validateEmail, validatePhone, getDiscount } from 'services/Utils';
import placeholder from 'assets/img/placeholder-partner.jpg';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import { ReactComponent as CopyIcon } from 'assets/svg/copy.svg';
import { ReactComponent as CopySuccessIcon } from 'assets/svg/copy-success.svg';
import { ReactComponent as ReceiptIcon } from 'assets/svg/receipt.svg';
import { ReactComponent as CategoryIcon } from 'assets/svg/partners/category.svg';
import { ReactComponent as EmailIcon } from 'assets/svg/partners/email.svg';
import { ReactComponent as LocationsIcon } from 'assets/svg/partners/locations.svg';
import { ReactComponent as PhoneIcon } from 'assets/svg/partners/phone.svg';
import { ReactComponent as SiteIcon } from 'assets/svg/partners/site.svg';
import { ReactComponent as StatesIcon } from 'assets/svg/partners/states.svg';
import { ReactComponent as LinkIcon } from 'assets/svg/partners/link.svg';

// Components
import Card from 'components/Card/Card';
import ButtonSelector from 'components/ButtonSelector/ButtonSelector';
import MapWithPoints from './components/maps/Single';
import LockedContent from '../discounts/components/LockedContent';

const PartnerModal = ({userData, item, isOpen, onOpen, onClose}) => {
	const [copied, setCopied] = useState(false);
	const bgIcon = 'rgba(9, 155, 255, 0.10)';
	const width = useBreakpointValue({ base: '100%', md: '1px' });
  	const height = useBreakpointValue({ base: '1px', md: '100%' });
  	const backgroundLineImage = useBreakpointValue({ 
  		base: 'repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0px, rgba(255, 255, 255, 0.10) 10px, transparent 10px, transparent 20px)', 
  		md: 'repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0px, rgba(255, 255, 255, 0.10) 10px, transparent 10px, transparent 20px)' 
  	});
  	const backgroundLineImageWhite = useBreakpointValue({ 
  		base: 'repeating-linear-gradient(90deg, #C8D3FF 0px, #C8D3FF 10px, transparent 10px, transparent 20px)', 
  		md: 'repeating-linear-gradient(0deg, #C8D3FF 0px, #C8D3FF 10px, transparent 10px, transparent 20px)' 
  	});
  	const bgDivider = useColorModeValue('white', '#0e1633');
  	const bgHalfCircleDivider = useColorModeValue('#CBE7FF', '#0e1325');
  	const borderColor = useColorModeValue('1px solid #CBE7FF', '1px solid #222840');
  	const textPartnerColor = useColorModeValue('#262D47', '#9FABCB');
  	const textColor = useColorModeValue('#262D47', '#E1E1E1');
  	const borderBottomInfo = useColorModeValue('1px solid #CBE7FF', '1px solid rgba(255, 255, 255, 0.10)');

  	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

	const [ modalTab, setModalTab ] = useState('details');

	const handleCopy = () => {
	    const text = document.querySelector('.partner-code')?.textContent; 

	    if (text) {
	        navigator.clipboard.writeText(text).then(() => {
	            setCopied(true);
	            setTimeout(() => setCopied(false), 7000); 
	        }).catch(err => console.error('Error copy:', err));
	    }
	};

	const plans = item?.discountInfo?.plans ?? {};
	const currentPlan = Object.values(plans).find(plan => plan?.isCurrent);

	const isMobile = useBreakpointValue({ base: true, md: false });

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
	        <ModalOverlay />
	        <ModalContent className="modal__simple modal_partner" maxW={{ sm: '800px' }}>
	        	<ModalCloseButton />
	        	<ButtonSelector
	                buttons={[
	                  { key: 'details', name: 'Details' },
	                  { key: 'locations', name: 'Locations' }
	                ]}
	                selected={modalTab}
	                onSelect={setModalTab}
	                variant="sm"
	                h="44px"
	                w={{base: '100%', sm: '361px'}}
	                m={{base: '32px auto 0 auto', md: '0px auto'}} />

	            { modalTab === 'details' ? (
	        	<Box 
	        		mt="24px" 
	        		position="relative" 
	        		overflow="hidden"
	        		h={{base: !userData?.id ? '200px' : 'auto', md: 'auto'}} 
	        		borderRadius={{base: !userData?.id ? '16px' : 'unset', md: 'unset'}} 
	        		mb="24px">
	        		<Grid 
			        	templateColumns={{ sm: '1fr', md: '1fr 40px 1fr'}} 
			        	gap="0px" 
			        	templateRows='1fr'>

			        	<Card padding="24px 16px" 
			        		borderRadius={{base: '16px 16px 0 0', md: '16px 0 0 16px'}} 
			        		borderRight={{ md: 'unset'}}
			        		borderBottom={{ base: 'unset', md: borderColor}}>
				            <Image 
				                src={item?.image ?? placeholder} 
				                alt="partner" 
				                borderRadius="16px"
				                maxH="156px"
				                w="100%"
				                h="100%"
				                objectFit="cover"
				                position="relative"
				                zIndex="1"
				            />
				            
				            <Text color={textPartnerColor} mt="16px">Partner</Text>
							<Text variant="title-lg" fontSize="22px" fontWeight="700" mt="2px">{ item?.name }</Text>
							{ userData?.id && (<>
							<Text variant="title-lg" fontSize="22px" fontWeight="700" mb="2px" color={textColor}>
							  {getDiscount(item)}
							</Text>

							<Box
			        			textAlign="left"
			        			zIndex="9"
			        			p="16px"
			        			mt="16px"
			        			borderRadius="16px"
								background={ isDark ? '#0D234D' : '#F5FAFF'}
								position="relative"
								overflow="hidden"
								border={ isDark ? 'unset' : '1px solid #CBE7FF'}
								boxShadow={ isDark ? '4px 4px 60px 0px rgba(0, 0, 0, 0.50)': 'unset'}>
								<Text variant="title-md" fontWeight="500">
									{ currentPlan?.description ?? item?.discountInfo?.title}
								</Text>

								{ currentPlan?.code && (
									<Flex
										mt="16px"
										justifyContent="space-between"
										alignItems="center"
										borderRadius="16px"
										position="relative"
										height="56px"
										fontWeight="800"
										padding="0px 16px"
										overflow="hidden"
										background="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
										boxShadow="0px 4px 12px 0px rgba(0, 122, 255, 0.50)">
										<Text variant="header" fontSize="20px" className="partner-code">
											{currentPlan?.code}
										</Text>

										<Box borderRadius="8px" bg="#0D234D" cursor="pointer" p="4px" onClick={handleCopy}>
    										{copied ? <CopySuccessIcon fontSize="23px" /> : <CopyIcon fontSize="23px" />}										</Box>

										<Box 
											boxShadow="-110px -1px 13px #ffffff"
											position="absolute" 
											width="100%"
										    height="2px"
										    left="0"
										    bottom="0"
											bg="linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 12.72%, #FAFDFE 23.59%, #FFF 30.15%, #38A9E1 48.09%, rgba(56, 169, 225, 0.00) 100%)" /> 
									</Flex>
								)}

								{ /*copied && (
									<Card padding="8px 12px" mt="16px">
										<Flex gap="12px" alignItems="center">
											<Box w="24px" h="24px"><ReceiptIcon /></Box>
											<Text fontSize="15px" color="white">
												Don`t forget to upload your Receipt with this code
											</Text>
										</Flex>
									</Card>
								)*/ }

								{ isDark ?
									<Lines className="abstract-lines" style={{ zIndex: '-1', top: '-230%', transform: 'rotate(259deg) scale(0.6)', right: '-30%', left: 'unset' }} />
									: <LinesWhite className="abstract-lines" style={{ zIndex: '-1', top: '-230%', transform: 'rotate(259deg) scale(0.6)', right: '-30%', left: 'unset', opacity: 0.5 }} />
								}
			        		</Box>

			        		<Flex justifyContent="center" gap="16px" mt="16px">
			        			{ item?.instagram && ( 
			        				<a href={item?.instagram }>
			        					<Box p="8px" borderRadius="full" bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
			        						<LuInstagram fontSize="28px" color="white" />
			        					</Box>
			        				</a> 
			        			)}

			        			{ item?.twitter && ( 
			        				<a href={item?.twitter }>
			        					<Box p="8px" borderRadius="full"  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
			        						<RiTwitterXFill fontSize="28px" color="white" />
			        					</Box>
			        				</a> 
			        			)}

			        			{ item?.facebook && ( 
			        				<a href={item?.facebook }>
			        					<Box p="8px" borderRadius="full"  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
			        						<MdFacebook fontSize="28px" color="white" />
			        					</Box>
			        				</a> 
			        			)}

			        			{ item?.youtube && ( 
			        				<a href={item?.youtube }>
			        					<Box p="8px" borderRadius="full"  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
			        						<FaYoutube fontSize="28px" color="white" />
			        					</Box>
			        				</a> 
			        			)}

			        			{ item?.tiktok && ( 
			        				<a href={item?.tiktok }>
			        					<Box p="8px" borderRadius="full"  bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
			        						<FaTiktok fontSize="28px" color="white" />
			        					</Box>
			        				</a> 
			        			)}
			        		</Flex>
			        		</>)}
			        	</Card>

			        	<Box
			        		transform={{sm: 'rotate(0deg)', md: 'rotate(0deg)'}}
			        	 	zIndex="1" bg={bgDivider}
			        	 	p={{base: '19px', md: '24px 19px'}}
			        	 	position="relative" overflow="hidden">
		        			<Box 
		        				bg={bgHalfCircleDivider} 
		        				border={borderColor}
		        				w="45px" 
		        				h="45px" 
		        				borderRadius="full" 
		        				position="absolute" 
		        				transform={{sm: 'rotate(0deg)', md: 'rotate(0deg)'}}
		        				top={{base: '-3px', md: '-32px'}} 
		        				left={{base: '-32px', md: '-3px'}}  />
			        		<Box
							  w={width}
							  h={height}
							  bg="transparent"
							  position="relative"
							  _before={{
							    content: '""',
							    position: 'absolute',
							    width,
							    height,
							    backgroundImage: isDark ? backgroundLineImage : backgroundLineImageWhite,
							  }}
							/>
							<Box 
								bg={bgHalfCircleDivider}
								border={borderColor}
								w="45px" 
								h="45px" 
								borderRadius="full" 
								position="absolute"
								transform={{sm: 'rotate(0deg)', md: 'rotate(0deg)'}} 
								bottom={{base: '-3px', md: '-32px'}} 
		        				left={{base: 'unset', md: '-3px'}} 
		        				right={{base: '-32px', md: 'unset'}} 
								 />
						</Box> 

						<Card 
							padding="24px 16px" 
							borderRadius={{base: '0 0 16px 16px', md: '0 16px 16px 0'}}
							borderLeft={{ md: 'unset'}}
			        		borderTop={{ base: 'unset', md: borderColor}}>
							<Flex gap="16px" mb="10px">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><CategoryIcon /></Box>
						        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400" borderBottom={borderBottomInfo}>
						          {item?.categories?.map(item => item.name).join(', ') || '-'}
						        </Text>
						    </Flex>

						    <Flex gap="16px" mb="10px">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><StatesIcon /></Box>
						        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400" borderBottom={borderBottomInfo}>
						          {item?.state ?? '-'}
						        </Text>
						    </Flex>

						    { userData?.id && (
							    <Box mt="5px" mb="16px">
						          { item?.tags?.map((item) => (
						            <Text key={item} class="tag-secondary">{item.name}</Text>
						          ))}
						        </Box>
					        )}

					        { userData?.id && (
						        <Text py="4px" mb="12px" lineHeight="21px" letterSpacing="-0.4px">
						         	{item?.description}
						        </Text>
					        )}

					        <Flex gap="16px" mb="10px" overflow="hidden">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><SiteIcon /></Box>
						        <Flex w="calc(100% - 48px)" pr="35px" gap="16px" borderBottom={borderBottomInfo}>
							        <Text isTruncated wordBreak="break-all" position="relative" mt="6px" pb="16px" color={textColor} w="100%" fontSize="15px" fontWeight="400">
							          {item?.website ?? '-'}
							        </Text>

							        { item?.website && (
							          	<a href={item?.website} style={{ position: 'absolute', right: '16px'}}>
							            	<LinkIcon style={{ marginTop: '3px', right: '0px'}} />
							            </a>
							        )}
						        </Flex>
						    </Flex>

						    <Flex gap="16px" mb="10px" overflow="hidden">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><EmailIcon /></Box>
						        <Flex w="calc(100% - 48px)" pr="35px" gap="16px" borderBottom={borderBottomInfo}>
							        <Text isTruncated position="relative" mt="6px" pb="16px" color={textColor} w="100%" fontSize="15px" fontWeight="400">
							          {item?.email ?? '-'}
							        </Text>

							        { item?.email && validateEmail(item?.email) && (
							          	<a href={`mailto:${item?.email}`} style={{ position: 'absolute', right: '16px'}}>
							            	<LinkIcon style={{ marginTop: '3px', right: '0px'}} />
							            </a>
							        )}
						        </Flex>
						    </Flex>

						    <Flex gap="16px" mb="10px" overflow="hidden">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><LocationsIcon /></Box>
						        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400" whiteSpace="unset" borderBottom={borderBottomInfo}>
						          { item?.locations?.length > 0 ? item?.locations?.length > 1 ? 
						          	`${item?.locations?.length} Locations` : item?.locations[0]?.name : item?.location ?? '-'
						          } 
						        </Text>
						    </Flex>

						    <Flex gap="16px" mb="10px" overflow="hidden">
						        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><PhoneIcon /></Box>
						        <Flex w="calc(100% - 48px)" pr="35px" gap="16px">
							        <Text isTruncated position="relative" mt="6px" pb="16px" color={textColor} w="100%" fontSize="15px" fontWeight="400">
							          {item?.contact ?? '-'}
							        </Text>

							        { item?.contact && validatePhone(item?.contact) && (
							          	<a href={`tel:${item?.contact}`} style={{ position: 'absolute', right: '16px'}}>
							            	<LinkIcon style={{ marginTop: '3px', right: '0px'}} />
							            </a>
							        )}
						        </Flex>
						    </Flex>
						</Card>

			        </Grid>

			        { !userData?.id && (<LockedContent />)}
	        	</Box>
	        	) : (
	        		<Card padding="0px" border="unset" my="24px">
	        			<Grid templateColumns={{ sm: '1fr', md: item?.locations?.length > 0 ? '0.8fr 1fr' : '1fr'}} gap="12px" templateRows='1fr'>
	        				
	        				{ isMobile && userData?.id && item?.locations?.length > 0 && <MapWithPoints data={item?.locations} item={item} 
	        					height="200px" borderRadius="16px 16px 0 0" /> }

		        			<Box p="16px 16px 0px 16px">
		        				<Flex gap="16px" mb="10px">
							        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><StatesIcon /></Box>
							        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400" borderBottom={borderBottomInfo}>
							          {item?.state ?? '-'}
							        </Text>
							    </Flex>
		        			
			        			<Flex gap="16px" mb="10px">
							        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><LocationsIcon /></Box>
							        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400" borderBottom={borderBottomInfo}>
							          { item?.locations?.length > 0 ? item?.locations?.length > 1 ? 
							          	`${item?.locations?.length} Locations` : item?.locations[0]?.name : item?.location ?? '-'
							          } 
							        </Text>
							    </Flex>

							    <Flex gap="16px" mb="10px">
							        <Box bg={bgIcon} borderRadius="8px" p="4px" h="32px"><PhoneIcon /></Box>
							        <Text position="relative" mt="6px" pb="16px" pr="25px" color={textColor} w="100%" fontSize="15px" fontWeight="400">
							          {item?.contact ?? '-'}
							          { item?.contact && validatePhone(item?.contact) && (
							          	<a href={`tel:${item?.contact}`}>
							            	<LinkIcon style={{ position: 'absolute', top: '-4px', right: '0px'}} />
							            </a>
							          )}
							        </Text>
							    </Flex>
						    </Box>

						    { !isMobile && userData?.id && item?.locations?.length > 0 && <MapWithPoints data={item?.locations} item={item} 
						    	height="550px" borderRadius="0px 16px 16px 0" /> }

					    </Grid>

					    { !userData?.id && (<LockedContent />)}
	        		</Card>
	        	)}

	        	{ userData?.id ? (<Button onClick={onClose} variant="secondary-md">Back</Button>) : (
	        		<Grid 
		        	templateColumns={{ sm: '1fr', md: '1fr 1fr'}} 
		        	gap="12px" 
		        	templateRows='1fr'>
		        		<Button onClick={onClose} variant="secondary-md">Back</Button>
		        		<NavLink to="/sign-up">
		        			<Button variant="button-md-g">Sing Up to Unlock</Button>
		        		</NavLink>
		        	</Grid>
	        	)}

	        </ModalContent>
	    </Modal> 
	);
};

export default PartnerModal;